import { useEffect, useMemo } from 'react'
import { useDeepMemo } from '@retailer-platform/shared-common'
import moment from 'moment-timezone'
import {
  InstacartCustomersSharedV1Locale,
  GetRetailerProductsMeshQueryVariables,
  useGetRetailerProductsMeshQuery,
  useGetRetailerProductsItemDataMeshLazyQuery,
  type GetRetailerProductsRequestParametersDateTimeRangeInput,
  type RetailerProductIdInput,
} from 'domains/products/api'
import {
  type ProductSearchParams,
  type ProductsSearchDatum,
} from 'domains/products/components/search/utils/types'
import { trackEvent } from 'utils/events/trackEvent'
import {
  SearchTypes,
  SearchError,
} from 'domains/products/components/search/productSearch.constants'
import { normalizeProductCodes } from 'utils/product-codes/normalizeProductCodes'
import { DateRangeOption } from 'gin-and-tonic/common/date-range-picker/DateRangePicker'
import useGetRetailerProductsVariables from './useGetRetailerProductsVariables.hooks'

export const parseSearchTerm = (searchParams: ProductSearchParams) => {
  const { searchTerm, searchType } = searchParams

  if (!searchTerm) {
    return
  }

  if (searchType === SearchTypes.Name) {
    return searchTerm && { query: searchTerm }
  } else if (searchType === SearchTypes.RRC) {
    const retailerReferenceCodes = searchTerm.split(',').map(e => e.trim())
    return retailerReferenceCodes && retailerReferenceCodes.length > 0
      ? { retailerReferenceCodes: retailerReferenceCodes }
      : undefined
  } else if (searchType === SearchTypes.UPC) {
    const upcs = searchTerm.split(/,| /).reduce((upcList: string[], input: string) => {
      const upc = input.trim()
      if (upc.length > 0) upcList.push(upc)
      return upcList
    }, [])
    const normalizedUpcs = normalizeProductCodes(upcs)

    return normalizedUpcs && normalizedUpcs.length > 0
      ? { productCodes: normalizedUpcs }
      : undefined
  } else if (searchType === SearchTypes.ProductId) {
    const productId = searchTerm.trim()
    if (productId && productId.length > 0) {
      const retailerProductId: RetailerProductIdInput = {
        retailerId: searchParams.retailerId,
        productId: productId,
      }
      return { retailerProductIds: [retailerProductId] }
    } else {
      return undefined
    }
  } else {
    throw Error(`Unexpected searchType ${searchType}`)
  }
}

export const parseCreationDate = (
  searchParams: ProductSearchParams
): GetRetailerProductsRequestParametersDateTimeRangeInput => {
  const { creationDateRange, creationDateCustomStart, creationDateCustomEnd } = searchParams

  if (!creationDateRange) {
    return
  }
  if (creationDateRange === 'custom') {
    //absolute dates
    return {
      from: creationDateCustomStart ? new Date(creationDateCustomStart) : undefined,
      to: creationDateCustomEnd ? new Date(creationDateCustomEnd) : undefined,
    }
  } else {
    //relative dates
    let daysAgo
    switch (creationDateRange) {
      case DateRangeOption.Last7Days:
        daysAgo = 7
        break
      case DateRangeOption.Last30Days:
        daysAgo = 30
        break
      case DateRangeOption.Last120Days:
        daysAgo = 120
        break
      case DateRangeOption.LastYear:
        daysAgo = 365
        break

      default:
        throw new Error('Unknown relative date range selected')
    }

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    const to = new Date() // now
    const from = new Date()
    from.setDate(to.getDate() - daysAgo) //subtract the relative days

    return {
      from: moment(from.getTime()).startOf('day').tz(tz).toDate(),
      to: moment(to.getTime()).endOf('day').tz(tz).toDate(),
    }
  }
}

export const validQuery = (
  searchParams: ProductSearchParams
): { isValid: boolean; searchError: SearchError } => {
  const { creationDateRange, creationDateCustomStart, creationDateCustomEnd } = searchParams

  const searchTerm = parseSearchTerm(searchParams)
  const missingQueryText = !searchTerm
  if (!missingQueryText) {
    let terms = []
    if (searchParams.searchType === SearchTypes.RRC) {
      terms = searchTerm.retailerReferenceCodes
    } else if (searchParams.searchType === SearchTypes.UPC) {
      terms = searchTerm.productCodes
    }
    if (terms.length > 100)
      return {
        isValid: false,
        searchError: SearchError.TooManySearchTerms,
      }
    if (searchParams.searchType === SearchTypes.ProductId) {
      for (const retailerProductId of searchTerm.retailerProductIds) {
        if (retailerProductId.productId && isNaN(Number(retailerProductId.productId))) {
          return {
            isValid: false,
            searchError: SearchError.InvalidProductId,
          }
        }
      }
    }
  }
  const missingCustomDates =
    creationDateRange == DateRangeOption.Custom &&
    (!creationDateCustomStart || !creationDateCustomEnd)

  return {
    isValid: !missingQueryText && !missingCustomDates,
    searchError: undefined,
  }
}

export const useGetRetailerProductsMesh = (props: ProductSearchParams) => {
  const variables = useGetRetailerProductsVariables(props)

  const searchError = variables.searchError

  const {
    data: retailerProductsData,
    loading: retailerProductsLoading,
    error: retailerProductsError,
    refetch,
  } = useGetRetailerProductsMeshQuery({
    variables: variables.result,
    skip: variables.skip,
  })
  const [
    executeItemDataQuery,
    { data: itemsData, loading: itemsLoading, error: itemsError, refetch: refetchItemsData },
  ] = useGetRetailerProductsItemDataMeshLazyQuery({ notifyOnNetworkStatusChange: true })

  useEffect(() => {
    if (
      retailerProductsLoading ||
      retailerProductsError ||
      !retailerProductsData?.retailerToolsServiceGetRetailerProducts?.results
    ) {
      return
    }

    // Trigger query to load item data when retailer product data has loaded
    const productIds = retailerProductsData.retailerToolsServiceGetRetailerProducts.results.map(
      ({ productId }) => productId
    )
    executeItemDataQuery({
      variables: {
        retailerId: props.retailerId,
        productIds,
      },
    })
  }, [
    retailerProductsData,
    retailerProductsLoading,
    retailerProductsError,
    props.retailerId,
    executeItemDataQuery,
  ])

  const data: ProductsSearchDatum[] = useMemo(() => {
    if (!retailerProductsData) {
      return null
    }
    if (retailerProductsLoading) {
      return []
    }

    const retailerProductsResults =
      retailerProductsData?.retailerToolsServiceGetRetailerProducts?.results
    const itemsResults =
      itemsData?.catalogToolsApiServiceGetRetailerProductData?.retailerProductData

    if (retailerProductsResults) {
      // Merge retailer product and item results where available
      if (itemsResults) {
        const itemsByProductId = Object.assign(
          {},
          ...itemsResults.map(item => ({
            [item.productId]: item,
          }))
        )

        return retailerProductsResults.map(product => ({
          ...product,
          ...itemsByProductId[product.productId],
        }))
      }

      return retailerProductsResults
    }

    return []
  }, [retailerProductsLoading, retailerProductsData, itemsData])

  const totalPages = useMemo(() => {
    const [totalItems, itemsPerPage] = [
      retailerProductsData?.retailerToolsServiceGetRetailerProducts?.totalCount,
      variables.result.limit,
    ].map(s => parseInt(s, 10))

    if (!totalItems || !itemsPerPage) return undefined

    return Math.ceil(totalItems / itemsPerPage)
  }, [variables.result.limit, retailerProductsData])

  const totalCount = retailerProductsData?.retailerToolsServiceGetRetailerProducts?.totalCount

  return {
    data,
    totalCount,
    retailerProductsLoading,
    retailerProductsError,
    itemsLoading,
    itemsError,
    refetchItemsData,
    totalPages,
    refetch,
    searchError,
  }
}
